/**
 * useOrganizations - Organization management hook
 *
 * Provides CRUD operations for organizations and member management.
 * Uses React Query for caching and background refresh.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';

const ORGS_KEY = ['organizations'] as const;
const orgKey = (token: string, id: string) => [...ORGS_KEY, token, id] as const;
const orgSubKey = (token: string, id: string) => [...ORGS_KEY, token, id, 'subscription'] as const;

export function useOrganizations(authToken: string) {
  return useQuery({
    queryKey: [...ORGS_KEY, authToken],
    queryFn: () => apiClient.listOrganizations(authToken),
    enabled: !!authToken,
    staleTime: 60_000,
    select: (data) => data.organizations ?? [],
  });
}

/** Returns the org ID the user owns, or null. Shares the same query cache as useOrganizations. */
export function useOwnedOrgId(authToken: string) {
  return useQuery({
    queryKey: [...ORGS_KEY, authToken],
    queryFn: () => apiClient.listOrganizations(authToken),
    enabled: !!authToken,
    staleTime: 60_000,
    select: (data) => data.ownedOrgId ?? null,
  });
}

export function useOrganization(authToken: string, orgId: string) {
  return useQuery({
    queryKey: orgKey(authToken, orgId),
    queryFn: () => apiClient.getOrganization(authToken, orgId),
    enabled: !!authToken && !!orgId,
    select: (data) => data.organization,
  });
}

export function useOrgSubscription(authToken: string, orgId: string) {
  return useQuery({
    queryKey: orgSubKey(authToken, orgId),
    queryFn: () => apiClient.getOrgSubscription(authToken, orgId),
    enabled: !!authToken && !!orgId,
    staleTime: 60_000,
    select: (data) => data.subscription,
  });
}

export function useCreateOrganization(authToken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; displayName?: string; description?: string }) =>
      apiClient.createOrganization(authToken, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORGS_KEY });
      toast.success('Organization created');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to create org'),
  });
}

export function useInviteOrgMember(authToken: string, orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; firstName?: string; lastName?: string }) =>
      apiClient.inviteOrgMember(authToken, orgId, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: orgKey(authToken, orgId) });
      toast.success(data.message);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to invite member'),
  });
}

export function useAddOrgMember(authToken: string, orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email?: string; userId?: string }) =>
      apiClient.addOrgMember(authToken, orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKey(authToken, orgId) });
      toast.success('Member added');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to add member'),
  });
}

export function useRemoveOrgMember(authToken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      apiClient.removeOrgMember(authToken, orgId, userId),
    onSuccess: (_, { orgId }) => {
      qc.invalidateQueries({ queryKey: ORGS_KEY });
      qc.invalidateQueries({ queryKey: orgKey(authToken, orgId) });
      toast.success('Member removed');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove member'),
  });
}

export function useDeleteOrganization(authToken: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => apiClient.deleteOrganization(authToken, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORGS_KEY });
      toast.success('Organization deleted');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete org'),
  });
}

export function useLinkOrgSubscription(authToken: string, orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.linkOrgSubscription(authToken, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgKey(authToken, orgId) });
      qc.invalidateQueries({ queryKey: orgSubKey(authToken, orgId) });
      toast.success('Subscription linked to organization');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Failed to link subscription'),
  });
}
